import {
  ILocationSectionFormValues,
  TLocationDropdownOption,
} from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/location-section.types'
import {
  extractStringFromHTML,
  mapFormWithQuill,
} from '@src/helpers/textHelpers'
import { API } from '@network'
import { FormikProps } from 'formik'
import { useEffect, useState } from 'react'

const MIN_ARRIVAL_INSTRUCTIONS_LENGTH = 80

export const useEditArrivalInstructions = (
  formik: FormikProps<ILocationSectionFormValues>,
  closeModal: () => void,
  modalSelectedLocation: TLocationDropdownOption,
  handleLocationChange: (
    location: TLocationDropdownOption,
    customArrivalInstructions?: string,
  ) => void,
  isConfirmStep?: boolean,
  location?: TLocationDropdownOption,
  property_id?: string,
) => {
  const [arrivalInstructions, setArrivalInstructions] = useState<string>()
  const [arrivalInstructionsErrors, setArrivalInstructionsErrors] = useState<
    string
  >()
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    setArrivalInstructions(location?.property_arrival_instructions)
  }, [location?.property_arrival_instructions])

  // Validate the arrival instructions
  useEffect(() => {
    if (arrivalInstructions === undefined) {
      return
    }

    const arrInstString = extractStringFromHTML(arrivalInstructions)

    if (!arrInstString) {
      setArrivalInstructionsErrors('Required')
      return
    }

    if (arrInstString.length < MIN_ARRIVAL_INSTRUCTIONS_LENGTH) {
      setArrivalInstructionsErrors(
        `Arrival Instructions must be at least ${MIN_ARRIVAL_INSTRUCTIONS_LENGTH} characters.`,
      )
      return
    }

    setArrivalInstructionsErrors(undefined)
  }, [arrivalInstructions])

  const handleEditArrivalInstructions = async () => {
    const propertyId = isConfirmStep
      ? modalSelectedLocation.value
      : Number(property_id)

    setIsSubmitting(true)
    try {
      const { setFieldValue, values } = formik

      // Apply mapFormWithQuill to clean up the arrivalInstructions
      const cleanedArrivalInstructions = mapFormWithQuill(
        { arrivalInstructions },
        ['arrivalInstructions'],
      ).arrivalInstructions

      await API.Properties.putProperty(propertyId, {
        arrival_instructions: cleanedArrivalInstructions,
      })

      if (isConfirmStep) {
        handleLocationChange(modalSelectedLocation, cleanedArrivalInstructions)
      } else {
        // Update the location object with the new arrival instructions
        if (values.location) {
          setFieldValue('location', {
            ...values.location,
            property_arrival_instructions: cleanedArrivalInstructions,
          })
        }
      }

      // Update key for property async dropdown in BasicShiftInfo via the main Formik state.
      // Updating the key will cause the Dropdown to rerender, fetch the properties (options) so we can have the edited one in the list
      // We need it in the list in case the user decides to re-select the property while still on the page after this PUT request
      setFieldValue(
        'keyForLocationsDropdown',
        values.keyForLocationsDropdown + 1,
      )

      closeModal()
    } catch (error) {
      setArrivalInstructionsErrors(error.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    arrivalInstructions,
    arrivalInstructionsErrors,
    setArrivalInstructions,
    handleEditArrivalInstructions,
    isSubmitting,
  }
}
